import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  DialogActions,
  IconButton,
  Snackbar,
  useTheme,
} from "@mui/material";
import * as XLSX from "xlsx";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "@apollo/client";
import { BULK_IMPORT_EMPLOYEES } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import { MasterLookupItemsType, AccountCategoryKey, EmploymentStatus, EmploymentStatusLabel } from "../../../../../../utils/enums";

const ImportExcelDialog = ({ open, onClose, refetch, onBack }) => {
  const theme = useTheme();
  const [jsonData, setJsonData] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const fileInputRef = useRef(null);
  const [bulkImportEmployees, { loading: isLoading }] = useMutation(BULK_IMPORT_EMPLOYEES);

  const validateAndSetFile = (selectedFile) => {
    const isValidFile = selectedFile?.name.endsWith(".xlsx") || selectedFile?.name.endsWith(".xls");

    if (selectedFile && isValidFile) {
      setFile(selectedFile);
      setError(false);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          
          const parsedData = XLSX.utils.sheet_to_json(sheet, {
            raw: true,
            defval: "",
          });

          setJsonData(parsedData);
        } catch (error) {
          console.error("Error parsing Excel:", error);
          setError(true);
          setJsonData(null);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    } else {
      setFile(null);
      setJsonData(null);
      setError(true);
    }
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = event.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const droppedFile = droppedFiles[0];
    validateAndSetFile(droppedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setJsonData(null);
    setError(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const mapExcelToEmployeeSchema = (excelData) => {
    return excelData
      .map((row) => {
        const employeeId = String(row["Employee ID"] || "").trim();
        const firstName = String(row["First Name (Mandatory, if not already in the system)"] || "").trim();
        const lastName = String(row["Last Name (Mandatory, if not already in the system)"] || "").trim();
        const email = String(row["Official Email ID (Mandatory, if not already in the system or Contact Number is not set)"] || "").trim();
        const startDate = row["Date of Joining(DD/MM/YYYY) (Mandatory, if not already in the system)"];

        // Validate required fields (joining date is optional)
        if (!employeeId || !firstName || !lastName || !email) {
          return null;
        }

        // Parse date (handle DD/MM/YYYY format)
        let parsedStartDate = null;
        if (startDate) {
          if (typeof startDate === 'number') {
            const excelEpoch = new Date(1899, 11, 30);
            parsedStartDate = new Date(excelEpoch.getTime() + startDate * 86400000);
          } else {
            const dateStr = String(startDate).trim();
            const ddmmyyyy = dateStr.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
            if (ddmmyyyy) {
              const day = parseInt(ddmmyyyy[1]);
              const month = parseInt(ddmmyyyy[2]) - 1;
              const year = parseInt(ddmmyyyy[3]);
              parsedStartDate = new Date(year, month, day);
            } else {
              parsedStartDate = new Date(dateStr);
            }
          }
          if (isNaN(parsedStartDate.getTime())) {
            parsedStartDate = null;
          }
        }

        const formattedDate = parsedStartDate
          ? `${parsedStartDate.getFullYear()}-${String(parsedStartDate.getMonth() + 1).padStart(2, "0")}-${String(parsedStartDate.getDate()).padStart(2, "0")}`
          : null;

        const employmentStatus = String(row["Employee Status"] || EmploymentStatusLabel.ACTIVE).trim().toLowerCase();
        
        let employee_type, employee_key, employment_status_value;
        if (employmentStatus === EmploymentStatusLabel.ACTIVE.toLowerCase()) {
          employee_type = MasterLookupItemsType.IN_SCOPE_USER_CATEGORY;
          employee_key = AccountCategoryKey.ACTIVE_USER;
          employment_status_value = EmploymentStatus.ACTIVE;
        } else if (employmentStatus === EmploymentStatusLabel.SERVICE_ACCOUNT.toLowerCase()) {
          employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
          employee_key = AccountCategoryKey.NON_PERSON;
          employment_status_value = EmploymentStatus.INACTIVE;
        } else if (employmentStatus === EmploymentStatusLabel.OFFBOARDED_USER.toLowerCase() || employmentStatus === EmploymentStatusLabel.TERMINATED.toLowerCase()) {
          employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
          employee_key = AccountCategoryKey.OFFBOARDED_USER;
          employment_status_value = EmploymentStatus.TERMINATED;
        } else {
          employee_type = MasterLookupItemsType.IN_SCOPE_USER_CATEGORY;
          employee_key = AccountCategoryKey.ACTIVE_USER;
          employment_status_value = EmploymentStatus.ACTIVE;
        }

        const role = row["Role"] || row["role"] || null;
        const employments = [
          {
            id: employeeId,
            job_title: role?.trim() || null,
            ...(formattedDate && { effective_date: formattedDate }),
            employment_type: "full-time",
          },
        ];

        return {
          employee_id: employeeId,
          first_name: firstName,
          last_name: lastName,
          name: `${firstName} ${lastName}`.trim(),
          email: email,
          start_date: formattedDate || "",
          employment_status: employment_status_value,
          employee_type,
          employee_key,
          employments,
          ...(row["Gender"] && {
            gender: String(row["Gender"]).trim() === "Male" ? "M" : String(row["Gender"]).trim() === "Female" ? "F" : "",
          }),
          ...(row["Termination Date"] && {
            termination_date: (() => {
              const dateValue = row["Termination Date"];
              if (!dateValue) return null;
              if (typeof dateValue === "number") {
                const excelEpoch = new Date(1899, 11, 30);
                const jsDate = new Date(excelEpoch.getTime() + dateValue * 86400000);
                if (isNaN(jsDate.getTime())) return null;
                return `${jsDate.getFullYear()}-${String(jsDate.getMonth() + 1).padStart(2, "0")}-${String(jsDate.getDate()).padStart(2, "0")}`;
              }
              if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
              }
              if (dateValue instanceof Date) {
                return dateValue.toISOString().substring(0, 10);
              }
              const dateStr = String(dateValue).trim();
              const ddmmyyyy = dateStr.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
              if (ddmmyyyy) {
                const day = parseInt(ddmmyyyy[1]);
                const month = parseInt(ddmmyyyy[2]) - 1;
                const year = parseInt(ddmmyyyy[3]);
                const date = new Date(year, month, day);
                if (isNaN(date.getTime())) return null;
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
              }
              const date = new Date(dateValue);
              if (isNaN(date.getTime())) return null;
              return date.toISOString().substring(0, 10);
            })(),
          }),
        };
      })
      .filter(Boolean);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const jsDate = new Date(excelEpoch.getTime() + dateValue * 86400000);
      return `${jsDate.getFullYear()}-${String(jsDate.getMonth() + 1).padStart(2, "0")}-${String(jsDate.getDate()).padStart(2, "0")}`;
    }
    const dateStr = String(dateValue).trim();
    const ddmmyyyy = dateStr.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (ddmmyyyy) {
      const day = parseInt(ddmmyyyy[1]);
      const month = parseInt(ddmmyyyy[2]) - 1;
      const year = parseInt(ddmmyyyy[3]);
      const date = new Date(year, month, day);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }
    return null;
  };

  const handleImport = async () => {
    if (!jsonData || jsonData.length === 0) return;

    try {
      const employees = mapExcelToEmployeeSchema(jsonData);
      
      if (employees.length === 0) {
        setSnackbar({
          message: "No valid employee data found. Please check required fields.",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }

      const response = await bulkImportEmployees({
        variables: { employees },
      });

      const result = response?.data?.bulkImportEmployees;
      
      if (result?.success) {
        setSnackbar({
          message: `Successfully imported ${result.successCount} of ${result.totalCount} employees.`,
          vertical: "top",
          horizontal: "right",
        });
        if (refetch) refetch();
        handleCancel();
      } else {
        setSnackbar({
          message: result?.message || "Import failed. Please try again.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      console.error("Error importing employees:", err);
      setSnackbar({
        message: err?.message || "Failed to import employees. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleCancel = () => {
    onClose();
    handleRemoveFile();
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        "Employee ID": "EMP001",
        "First Name (Mandatory, if not already in the system)": "John",
        "Last Name (Mandatory, if not already in the system)": "Doe",
        "Official Email ID (Mandatory, if not already in the system or Contact Number is not set)": "john.doe@example.com",
        "Date of Joining(DD/MM/YYYY) (Mandatory, if not already in the system)": "01/01/2024",
        "Employee Status": "Active",
        "Role": "CEO",
        "Gender": "Male",
        "Termination Date": ""
      },
      {
        "Employee ID": "EMP002",
        "First Name (Mandatory, if not already in the system)": "Jane",
        "Last Name (Mandatory, if not already in the system)": "Smith",
        "Official Email ID (Mandatory, if not already in the system or Contact Number is not set)": "jane.smith@example.com",
        "Date of Joining(DD/MM/YYYY) (Mandatory, if not already in the system)": "15/03/2024",
        "Employee Status": "Active",
        "Role": "SDE1",
        "Gender": "Female",
        "Termination Date": ""
      }
    ];

    const wb = XLSX.utils.book_new();
    
    const ws = XLSX.utils.json_to_sheet(sampleData);
    
    const colWidths = [
      { wch: 15 }, // Employee ID
      { wch: 50 }, // First Name
      { wch: 50 }, // Last Name
      { wch: 60 }, // Email
      { wch: 30 }, // Date of Joining
      { wch: 15 }, // Employee Status
      { wch: 15 }, // Role
      { wch: 10 }, // Gender
      { wch: 20 }, // Termination Date
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    
    XLSX.writeFile(wb, "employee_import_template.xlsx");
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth>
      <DialogTitle fontWeight="bold">
        Import Employees from Excel
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>


        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Download the sample file with all required columns to import employees.{" "}
          <Typography
            component="span"
            onClick={handleDownloadSample}
            sx={{
              color: "#031B89",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 500,
              "&:hover": {
                color: "#020d5c",
              },
            }}
          >
            Download Sample
          </Typography>
        </Typography>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: "1px dashed",
            borderColor: error ? "#C71414" : isDragOver ? "#031B89" : "#031B89",
            backgroundColor: isDragOver ? "rgba(3, 27, 137, 0.04)" : "inherit",
            borderRadius: 2,
            py: 2,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          <CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: "#031B89" }} />
          <Typography
            variant="body2"
            sx={{ color: error ? "#C71414" : "#031B89", fontWeight: 500 }}
          >
            Drag and drop an Excel file here, or click to browse
          </Typography>
          <Typography
            variant="caption"
            sx={{ mt: 0.5, color: "text.secondary", textAlign: "center" }}
          >
            Allowed file types: .xlsx, .xls
          </Typography>
          <input
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={handleFileUpload}
            ref={fileInputRef}
          />
        </Box>

        {file && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <AttachFileIcon sx={{ color: "#031B89", mr: 1 }} />
            <Typography variant="body2" sx={{ color: "#031B89", mr: 1 }}>
              {file.name}
            </Typography>
            <IconButton onClick={handleRemoveFile} size="small">
              <DeleteIcon fontSize="small" sx={{ color: "#031B89" }} />
            </IconButton>
          </Box>
        )}

        {error && !file && (
          <Typography variant="body2" color="#C71414" sx={{ mt: 1 }}>
            Please select a valid Excel file (.xlsx or .xls).
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            fontSize: "14px",
            color: theme.customColors.text.secondary,
            borderColor: theme.customColors.border.main,
            "&:hover": {
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
        {onBack && (
          <Button
            onClick={onBack}
            variant="outlined"
            sx={{
              fontSize: "14px",
              color: theme.customColors.text.secondary,
              borderColor: theme.customColors.border.main,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            Back
          </Button>
        )}
        <LoadingButton
          variant="contained"
          disabled={!file || error || !jsonData}
          loading={isLoading}
          onClick={handleImport}
          sx={{
            backgroundColor: theme.customColors.button.hover,
            "&:hover": { backgroundColor: theme.customColors.button.primary },
            fontSize: "14px",
          }}
        >
          Import
        </LoadingButton>
      </DialogActions>

      {snackbar && (
        <Snackbar
          open={!!snackbar}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
          message={snackbar.message}
          anchorOrigin={
            snackbar.vertical && snackbar.horizontal
              ? { vertical: snackbar.vertical, horizontal: snackbar.horizontal }
              : { vertical: "bottom", horizontal: "center" }
          }
        />
      )}
    </Dialog>
  );
};

export default ImportExcelDialog;

